import { useState } from "react";
import { api, type Tag } from "../api.ts";

const KEY_MAX = 32;
const VALUE_MAX = 128;

interface TagRow {
  key: string;
  value: string;
}

export function AddMediaModal({
  project,
  onClose,
  onAdded,
}: {
  project: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<TagRow[]>([{ key: "", value: "" }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setRow(i: number, patch: Partial<TagRow>) {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setRows((rs) => [...rs, { key: "", value: "" }]);
  }
  function removeRow(i: number) {
    setRows((rs) => (rs.length === 1 ? [{ key: "", value: "" }] : rs.filter((_, idx) => idx !== i)));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) {
      setError("Choose a media file first.");
      return;
    }
    const tags: Tag[] = rows
      .map((r) => ({ key: r.key.trim(), value: r.value }))
      .filter((r) => r.key.length > 0);

    setBusy(true);
    setError(null);
    try {
      await api.addMedia(project, file, tags);
      onAdded();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 text-lg font-semibold text-slate-100">Add media</h2>

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="mb-1 block text-sm text-slate-400">Media file</label>
            <input
              type="file"
              accept="audio/*,video/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-slate-300 file:mr-3 file:rounded-md file:border-0 file:bg-slate-800 file:px-3 file:py-2 file:text-slate-200 hover:file:bg-slate-700"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm text-slate-400">Tags</label>
              <button
                type="button"
                onClick={addRow}
                className="text-xs text-indigo-400 hover:text-indigo-300"
              >
                + Add tag
              </button>
            </div>
            <div className="space-y-2">
              {rows.map((row, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    value={row.key}
                    maxLength={KEY_MAX}
                    onChange={(e) => setRow(i, { key: e.target.value })}
                    placeholder="key"
                    className="w-1/3 rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 outline-none focus:border-indigo-500"
                  />
                  <input
                    value={row.value}
                    maxLength={VALUE_MAX}
                    onChange={(e) => setRow(i, { value: e.target.value })}
                    placeholder="value"
                    className="flex-1 rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 outline-none focus:border-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    className="rounded-md border border-slate-700 px-2 text-slate-500 hover:text-rose-400"
                    aria-label="Remove tag"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <p className="mt-1 text-xs text-slate-600">
              key ≤ {KEY_MAX} chars, value ≤ {VALUE_MAX} chars. Blank rows are ignored.
            </p>
          </div>

          {error && <p className="text-sm text-rose-400">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !file}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
            >
              {busy ? "Processing…" : "Upload & fingerprint"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
